/**
 * Blog article JSON-LD (@graph) builder for SEO and AEO.
 * Shared between public website and API prerender.
 */

export interface SchemaThing {
  '@type': 'Thing' | string;
  name: string;
  sameAs?: string;
}

export interface BlogSchemaAuthorInput {
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  profileUrl?: string;
  socialUrls?: string[];
}

export interface BlogSchemaFaqItem {
  question: string;
  answer: string;
}

export interface BuildBlogArticleGraphSchemaInput {
  title: string;
  description?: string;
  excerpt?: string;
  contentHtml?: string;
  url: string;
  baseUrl: string;
  inLanguage?: string;
  images?: string[];
  author?: BlogSchemaAuthorInput;
  publisherName: string;
  publisherLogoUrl?: string;
  datePublished: string;
  dateModified?: string;
  category?: string;
  keywords?: string[];
  readingTime?: number;
  wordCount?: number;
  faq?: BlogSchemaFaqItem[];
  aboutEntities?: SchemaThing[];
  schemaEnrichment?: Record<string, unknown>;
}

const HTML_ENTITY_MAP: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

export function stripHtmlToPlainText(html: string): string {
  if (!html) return '';

  let text = html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  for (const [entity, char] of Object.entries(HTML_ENTITY_MAP)) {
    text = text.split(entity).join(char);
  }

  text = text.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)));
  text = text.replace(/&#x([0-9a-f]+);/gi, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );

  return text.replace(/\s+/g, ' ').trim();
}

export function countWords(text: string): number {
  if (!text) return 0;
  return (text.match(/\S+/g) || []).length;
}

export function normalizeSeoDescription(
  text: string,
  minLength = 120,
  maxLength = 160
): string {
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';

  if (cleaned.length <= maxLength) {
    if (cleaned.length >= minLength || cleaned.length < minLength) {
      const lastPunctuation = Math.max(
        cleaned.lastIndexOf('.'),
        cleaned.lastIndexOf('!'),
        cleaned.lastIndexOf('?')
      );
      if (lastPunctuation >= minLength - 1) {
        return cleaned.slice(0, lastPunctuation + 1).trim();
      }
    }
    return cleaned;
  }

  const slice = cleaned.slice(0, maxLength);
  const lastSpace = slice.lastIndexOf(' ');
  const truncated = lastSpace > minLength ? slice.slice(0, lastSpace) : slice;

  const lastPunctuation = Math.max(
    truncated.lastIndexOf('.'),
    truncated.lastIndexOf('!'),
    truncated.lastIndexOf('?')
  );

  if (lastPunctuation >= minLength - 20) {
    return truncated.slice(0, lastPunctuation + 1).trim();
  }

  return `${truncated.trim()}...`;
}

export function formatSchemaDate(iso: string, timezoneOffset = '+02:00'): string {
  if (!iso) return iso;

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  if (/[zZ]$/.test(iso) || /[+-]\d{2}:\d{2}$/.test(iso)) {
    return date.toISOString();
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  const match = timezoneOffset.match(/^([+-])(\d{2}):(\d{2})$/);
  if (!match) return date.toISOString();

  const sign = match[1] === '-' ? -1 : 1;
  const offsetHours = parseInt(match[2], 10);
  const offsetMinutes = parseInt(match[3], 10);
  const offsetMs = sign * (offsetHours * 60 + offsetMinutes) * 60 * 1000;
  const local = new Date(date.getTime() + offsetMs);

  return `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}T${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}:${pad(local.getUTCSeconds())}${timezoneOffset}`;
}

export function resolveAbsoluteUrl(path: string, baseUrl: string): string {
  if (!path) return baseUrl;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = baseUrl.replace(/\/$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized}`;
}

export function buildPublisherOrg(options: {
  appName: string;
  baseUrl: string;
  logoUrl?: string;
}) {
  const publisher: Record<string, unknown> = {
    '@type': 'Organization',
    name: options.appName,
    url: options.baseUrl,
  };

  if (options.logoUrl) {
    publisher.logo = {
      '@type': 'ImageObject',
      url: resolveAbsoluteUrl(options.logoUrl, options.baseUrl),
    };
  }

  return publisher;
}

export function buildAuthorPerson(author: BlogSchemaAuthorInput, baseUrl: string) {
  const name = [author.firstName, author.lastName].filter(Boolean).join(' ').trim();
  if (!name) return undefined;

  const person: Record<string, unknown> = {
    '@type': 'Person',
    name,
  };

  if (author.jobTitle) {
    person.jobTitle = author.jobTitle;
  }

  const sameAs = (author.socialUrls || []).filter(Boolean);
  if (sameAs.length > 0) {
    person.sameAs = sameAs;
  }

  // Google recommends pointing to a page that uniquely identifies the author (e.g. LinkedIn).
  // If the author has social URLs, prioritize the preferred one (LinkedIn, Facebook, Twitter) as the primary URL.
  // Otherwise, use their website profile URL, correcting legacy invalid '/team/:id' to the active query route '/team?author=:id'.
  const preferredSocial = sameAs.find(
    (url) => url.includes('linkedin.com') || url.includes('facebook.com') || url.includes('twitter.com')
  ) || sameAs[0];

  if (preferredSocial) {
    person.url = preferredSocial;
  } else if (author.profileUrl) {
    let finalProfileUrl = author.profileUrl;
    if (/\/team\/\d+$/.test(author.profileUrl)) {
      const match = author.profileUrl.match(/\/team\/(\d+)$/);
      if (match) {
        finalProfileUrl = `/team?author=${match[1]}`;
      }
    }
    person.url = resolveAbsoluteUrl(finalProfileUrl, baseUrl);
  }

  return person;
}

export function extractAboutEntities(
  schemaEnrichment?: Record<string, unknown>,
  tags?: string[]
): SchemaThing[] | undefined {
  const fromEnrichment = schemaEnrichment?.about;
  if (Array.isArray(fromEnrichment) && fromEnrichment.length > 0) {
    return fromEnrichment
      .filter((item): item is SchemaThing => {
        return (
          typeof item === 'object' &&
          item !== null &&
          typeof (item as SchemaThing).name === 'string'
        );
      })
      .map((item) => ({
        '@type': item['@type'] || 'Thing',
        name: item.name,
        ...(item.sameAs ? { sameAs: item.sameAs } : {}),
      }));
  }

  if (tags && tags.length > 0) {
    return tags.slice(0, 5).map((tag) => ({
      '@type': 'Thing',
      name: tag,
    }));
  }

  return undefined;
}

/**
 * Normalize AI output: keep only enrichment (about/mentions), discard flat BlogPosting overrides.
 */
export function normalizeSchemaEnrichmentFromAi(
  schemaMarkup?: Record<string, unknown>,
  schemaEnrichment?: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (schemaEnrichment && Object.keys(schemaEnrichment).length > 0) {
    return schemaEnrichment;
  }

  if (!schemaMarkup) return undefined;

  if (schemaMarkup['@graph'] && Array.isArray(schemaMarkup['@graph'])) {
    const graph = schemaMarkup['@graph'] as Record<string, unknown>[];
    const article = graph.find(
      (node) => node['@type'] === 'BlogPosting' || node['@type'] === 'Article'
    );
    if (article?.about) {
      return { about: article.about };
    }
    if (article?.mentions) {
      return { mentions: article.mentions };
    }
    return undefined;
  }

  const enrichment: Record<string, unknown> = {};
  if (schemaMarkup.about) enrichment.about = schemaMarkup.about;
  if (schemaMarkup.mentions) enrichment.mentions = schemaMarkup.mentions;

  if (Object.keys(enrichment).length > 0) {
    return enrichment;
  }

  return undefined;
}

export function buildBlogArticleGraphSchema(input: BuildBlogArticleGraphSchemaInput) {
  const pageUrl = resolveAbsoluteUrl(input.url, input.baseUrl);
  const plainText = stripHtmlToPlainText(input.contentHtml || '');
  const wordCount = input.wordCount ?? countWords(plainText);

  const descriptionSource =
    input.description ||
    input.excerpt ||
    plainText;
  const description = normalizeSeoDescription(descriptionSource);

  const images = (input.images || [])
    .filter(Boolean)
    .map((img) => resolveAbsoluteUrl(img, input.baseUrl));

  const enrichment = input.schemaEnrichment || {};
  const about =
    input.aboutEntities ||
    extractAboutEntities(enrichment, input.keywords);

  const blogPosting: Record<string, unknown> = {
    '@type': 'BlogPosting',
    '@id': `${pageUrl}#article`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
    headline: input.title,
    description,
    url: pageUrl,
    inLanguage: input.inLanguage || 'ro-RO',
    datePublished: formatSchemaDate(input.datePublished),
    publisher: buildPublisherOrg({
      appName: input.publisherName,
      baseUrl: input.baseUrl,
      logoUrl: input.publisherLogoUrl,
    }),
  };

  if (images.length > 0) {
    blogPosting.image = images;
  }

  const author = input.author ? buildAuthorPerson(input.author, input.baseUrl) : undefined;
  if (author) {
    blogPosting.author = author;
  }

  if (input.dateModified) {
    blogPosting.dateModified = formatSchemaDate(input.dateModified);
  }

  if (input.category) {
    blogPosting.articleSection = input.category;
  }

  if (input.keywords && input.keywords.length > 0) {
    blogPosting.keywords = input.keywords.join(', ');
  }

  if (wordCount > 0) {
    blogPosting.wordCount = wordCount;
  }

  if (input.readingTime && input.readingTime > 0) {
    blogPosting.timeRequired = `PT${input.readingTime}M`;
  }

  if (plainText) {
    blogPosting.articleBody = plainText;
  }

  if (about && about.length > 0) {
    blogPosting.about = about;
  }

  const graph: Record<string, unknown>[] = [blogPosting];

  if (input.faq && input.faq.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${pageUrl}#faq`,
      isPartOf: { '@id': pageUrl },
      mainEntity: input.faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: stripHtmlToPlainText(item.answer),
        },
      })),
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}
